/* eslint-disable no-console */
/* eslint-disable tsdoc/syntax */
/* eslint-disable jsdoc/require-example */
const CONFIG = {
  defaultOptions: {
    colorize: true,
    label: '',
    printOutput: true,
    showIcons: false,
  },
  levels: {
    error: {
      color: 'red',
      icon: String.fromCodePoint(0x26d4),
      label: 'error',
    },
    info: {
      color: 'blue',
      icon: String.fromCodePoint(0x2139),
      label: 'info',
    },
    log: {
      color: 'charcoal',
      icon: String.fromCodePoint(0x1f4dd),
      label: 'log',
    },
    success: {
      color: 'green',
      icon: String.fromCodePoint(0x2705),
      label: 'success',
    },
    warn: {
      color: 'orange',
      icon: String.fromCodePoint(0x26a0),
      label: 'warn',
    },
  },
  localStorageKeys: {
    colorize: 'YouVersion:LogUtility:Colorize',
    label: 'YouVersion:LogUtility:Label',
    printOutput: 'YouVersion:LogUtility:PrintOutput',
    showIcons: 'YouVersion:LogUtility:ShowIcons',
  },
};

/**
 * Utility to configure and output messages to the browser console.
 *
 * @author Anthony Hessler <anthony.hessler@life.church>
 * @version 1.0
 */
export function Log() {}

/**
 * Format and return a list of args from the specified parameters.
 *
 * @param {object} level - The `level` object from `CONFIG` to use for formatting.
 * @param {*} message - String or object to output.
 * @param  {...any} params - Optional list of objects to output.
 * @returns List of formatted args.
 */
function formatLogArgs(level, message, ...params) {
  const config = Log.options();

  // If message is string, we want to output it as such. Otherwise, set arg1
  // to blank string and immediately add to start of returned args. This is
  // necessary for properly outputting objects as the `message` parameter.
  const arg1 = typeof message === 'string' ? ` ${message}` : '';
  const argN = Array.prototype.slice.call(...params);
  const icon = `${config.showIcons ? `${level.icon} ` : ''}`;
  const label = `${config.label ? `${config.label} ` : ''}`;
  if (typeof message !== 'string') {
    argN.unshift(message);
  }

  // Add arg1, icon, and label to the start of returned args, wrapping with
  // appropriate strings and styles if `config.colorize` is `true`.
  if (config.colorize) {
    argN.unshift(`${config.colorize ? `%c` : ''}${icon}${label}${arg1}`, `color: ${level.color};`);
  } else {
    argN.unshift(`${icon}${label}${arg1}`);
  }
  return argN;
}

/**
 * Configure the Log utility with the specified options.
 *
 * @param {object} [options] - The object of configuration options.
 * @param {boolean} [options.colorize] - If true, will colorize the message outputs.
 * @param {boolean} [options.label] - A label to prepend to the message outputs.
 * @param {boolean} [options.printOutput] - If true, will print the message to the console.
 * @param {boolean} [options.showIcons] - If true, will include an emoji icon in the message outputs.
 */
Log.configure = (options = CONFIG.defaultOptions) => {
  localStorage.setItem(CONFIG.localStorageKeys.colorize, options.colorize);
  localStorage.setItem(CONFIG.localStorageKeys.label, options.label || CONFIG.defaultOptions.label);
  localStorage.setItem(CONFIG.localStorageKeys.printOutput, options.printOutput);
  localStorage.setItem(CONFIG.localStorageKeys.showIcons, options.showIcons);
};

/**
 * Retrieve the configuration options for the Log utility.
 *
 * @returns {object} Object of configuration options for the Log utility.
 */
Log.options = () => {
  return {
    colorize: localStorage.getItem(CONFIG.localStorageKeys.colorize)
      ? localStorage.getItem(CONFIG.localStorageKeys.colorize).toString() === 'true'
      : CONFIG.defaultOptions.colorize,
    label: localStorage.getItem(CONFIG.localStorageKeys.label)
      ? localStorage.getItem(CONFIG.localStorageKeys.label).toString()
      : CONFIG.defaultOptions.label,
    printOutput: localStorage.getItem(CONFIG.localStorageKeys.printOutput)
      ? localStorage.getItem(CONFIG.localStorageKeys.printOutput).toString() === 'true'
      : CONFIG.defaultOptions.printOutput,
    showIcons: localStorage.getItem(CONFIG.localStorageKeys.showIcons)
      ? localStorage.getItem(CONFIG.localStorageKeys.showIcons).toString() === 'true'
      : CONFIG.defaultOptions.showIcons,
  };
};

/**
 * Output the specified error message to the console.
 *
 * @param {*} message - The main string or object to output to the console.
 * @param {...any} [params] - A list of optional objects to output to the console. The string representations of each of these objects are appended together in the order listed and output.
 */
Log.error = (message, ...params) => {
  const config = Log.options();
  if (config.printOutput) {
    const args = formatLogArgs(CONFIG.levels.error, message, params);
    console.error(...args);
  }
};

/**
 * Output the specified info message to the console.
 *
 * @param {*} message - The main string or object to output to the console.
 * @param {...any} [params] - A list of optional objects to output to the console. The string representations of each of these objects are appended together in the order listed and output.
 */
Log.info = (message, ...params) => {
  const config = Log.options();
  if (config.printOutput) {
    const args = formatLogArgs(CONFIG.levels.info, message, params);
    console.info(...args);
  }
};

/**
 * Output the specified log message to the console.
 *
 * @param {*} message - The main string or object to output to the console.
 * @param {...any} [params] - A list of optional objects to output to the console. The string representations of each of these objects are appended together in the order listed and output.
 */
Log.log = (message, ...params) => {
  const config = Log.options();
  if (config.printOutput) {
    const args = formatLogArgs(CONFIG.levels.log, message, params);
    console.log(...args);
  }
};

/**
 * Output the specified success message to the console.
 *
 * @param {*} message - The main string or object to output to the console.
 * @param {...any} [params] - A list of optional objects to output to the console. The string representations of each of these objects are appended together in the order listed and output.
 */
Log.success = (message, ...params) => {
  const config = Log.options();
  if (config.printOutput) {
    const args = formatLogArgs(CONFIG.levels.success, message, params);
    console.log(...args);
  }
};

/**
 * Output the specified warn message to the console.
 *
 * @param {*} message - The main string or object to output to the console.
 * @param {...any} [params] - A list of optional objects to output to the console. The string representations of each of these objects are appended together in the order listed and output.
 */
Log.warn = (message, ...params) => {
  const config = Log.options();
  if (config.printOutput) {
    const args = formatLogArgs(CONFIG.levels.warn, message, params);
    console.warn(...args);
  }
};
